import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';

import { useAlert } from '../Components/AlertContext';
import Table from '../Components/Tables';
import { TaskScheduleApiProvider, useTaskScheduleApi } from '../Services/TaskScheduleApiProvider';
import {
  TaskScheduleApi,
  TaskScheduleExecution,
  TaskScheduleExecutionsApi,
  TaskScheduleUpdateApi,
} from '../Services/TaskScheduleApiProviderTypes';
import { formatApiDateToString } from '../utils/formatDate';
import { scheduleUtcToLocal } from '../utils/scheduleTimezone';
import TaskScheduleForm from './TaskScheduleForm';
import {
  executionColumns,
  TaskScheduleForm as FormState,
  getChangedFields,
  INITIAL_FORM_STATE,
  validate,
} from './TaskScheduleHelpers';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

function TaskScheduleDetailsContent() {
  const navigate = useNavigate();

  const { organizationId, taskScheduleId } = useParams();
  const { showAlert } = useAlert();

  const { updateTaskSchedule, getAssistants, getTaskScheduleById, getTaskScheduleExecutions } =
    useTaskScheduleApi();

  const [loading, setLoading] = useState(false);

  const [agents, setAgents] = useState([]);
  const [executions, setExecutions] = useState<TaskScheduleExecution[]>([]);

  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE);
  const [initialForm, setInitialForm] = useState<FormState | null>(null);

  const [totalExecutions, setTotalExecutions] = useState<number>(0);

  const tableTitle = 'Task Executions';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const fetchTaskSchedule = async () => {
    const taskSchedule: TaskScheduleApi = await getTaskScheduleById(taskScheduleId);

    const formData = {
      name: taskSchedule.name,
      assistantKey: taskSchedule.assistant_key,
      startDate: formatApiDateToString(taskSchedule.start_date),
      endDate: formatApiDateToString(taskSchedule.end_date),
      taskDefinition: taskSchedule.task_definition ?? '',
      schedule: scheduleUtcToLocal(taskSchedule.schedule || {}),
    };

    setForm(formData);
    setInitialForm(formData);
  };

  const fetchExecutions = async (skip: number, take: number) => {
    const executionsData: TaskScheduleExecutionsApi = await getTaskScheduleExecutions(
      taskScheduleId,
      skip,
      take,
    );

    setExecutions(executionsData.items);
    setTotalExecutions(executionsData.totalCount);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        await Promise.all([fetchTaskSchedule(), fetchExecutions(persistedSkip, persistedPageSize)]);
      } catch (error) {
        showAlert('fail', 'Failed to fetch task schedule');
      } finally {
        setLoading(false);
      }
    };

    if (taskScheduleId) {
      load();
    }
  }, [taskScheduleId]);

  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        const assistants = await getAssistants();
        setAgents(assistants);
      } catch (error) {
        showAlert('fail', 'Failed to fetch assistants');
      }
    };

    fetchAssistants();
  }, []);

  const onSave = async () => {
    if (!validate(form, showAlert)) return;

    try {
      setLoading(true);

      const changes: TaskScheduleUpdateApi = getChangedFields(initialForm, form);

      if (!Object.keys(changes).length) {
        showAlert('fail', 'No changes to save');
        return;
      }

      await updateTaskSchedule(taskScheduleId, changes);
      setInitialForm(form);
      showAlert('success', 'Task schedule updated successfully!');
    } catch (error) {
      showAlert('fail', 'Failed to update task schedule');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/task-schedule`);
  };

  const handleRowClick = (row: TaskScheduleExecution) => {
    navigate(
      `/organizations/${organizationId}/task-schedule/${taskScheduleId}/executions/${row.id}?assistantId=${row.assistant_key}`,
    );
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{form.name}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>
              <TaskScheduleForm form={form} agents={agents} onChange={setForm} />

              <div className="input-block d-flex flex-column justify-between">
                <Table
                  title={tableTitle}
                  columns={executionColumns}
                  data={executions}
                  selectableRowsHighlight
                  onRowClicked={handleRowClick}
                  paginationPerPage={persistedPageSize}
                  paginationTotalRows={totalExecutions}
                  onChangePage={fetchExecutions}
                />
              </div>
            </div>
            <div className="buttons-container justify-end gap-12">
              <Button background="color2" size="medium" onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskScheduleDetails() {
  return (
    <TaskScheduleApiProvider>
      <TaskScheduleDetailsContent />
    </TaskScheduleApiProvider>
  );
}

export default TaskScheduleDetails;
